import React, { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { SettingsProvider } from './contexts/SettingsContext';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';

// Every route is code-split into its own chunk so a storefront visitor's first load only
// downloads the page they're actually on — the /admin subtree in particular (11 separate
// dashboard pages) previously rode along in every customer's bundle even though they'd
// never open it.
const HomePage = lazy(() => import('./pages/HomePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PaymentCallbackPage = lazy(() => import('./pages/PaymentCallbackPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const LessonsPage = lazy(() => import('./pages/LessonsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminOffers = lazy(() => import('./pages/admin/AdminOffers'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminLessons = lazy(() => import('./pages/admin/AdminLessons'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));

const WithLayout = ({ children }) => <Layout>{children}</Layout>;

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <Loader2 className="w-7 h-7 animate-spin text-[#171412]" />
  </div>
);

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <DataProvider>
            <SettingsProvider>
              <CartProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<WithLayout><HomePage /></WithLayout>} />
                    <Route path="/shop" element={<WithLayout><ShopPage /></WithLayout>} />
                    <Route path="/category/:slug" element={<WithLayout><ShopPage /></WithLayout>} />
                    <Route path="/product/:id" element={<WithLayout><ProductDetailPage /></WithLayout>} />
                    <Route path="/cart" element={<WithLayout><CartPage /></WithLayout>} />
                    <Route path="/checkout" element={<WithLayout><CheckoutPage /></WithLayout>} />
                    <Route path="/payment/callback" element={<WithLayout><PaymentCallbackPage /></WithLayout>} />
                    <Route path="/login" element={<WithLayout><LoginPage /></WithLayout>} />
                    <Route path="/register" element={<WithLayout><RegisterPage /></WithLayout>} />
                    <Route path="/account" element={<WithLayout><AccountPage /></WithLayout>} />
                    <Route path="/lessons" element={<WithLayout><LessonsPage /></WithLayout>} />
                    <Route path="/lessons/:slug" element={<WithLayout><LessonsPage /></WithLayout>} />
                    <Route path="/about" element={<WithLayout><AboutPage /></WithLayout>} />
                    <Route path="/contact" element={<WithLayout><ContactPage /></WithLayout>} />

                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="products" element={<AdminProducts />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="categories" element={<AdminCategories />} />
                      <Route path="offers" element={<AdminOffers />} />
                      <Route path="coupons" element={<AdminCoupons />} />
                      <Route path="reviews" element={<AdminReviews />} />
                      <Route path="lessons" element={<AdminLessons />} />
                      <Route path="reports" element={<AdminReports />} />
                      <Route path="settings" element={<AdminSettings />} />
                    </Route>
                  </Routes>
                  </Suspense>
                </BrowserRouter>
              </CartProvider>
            </SettingsProvider>
            </DataProvider>
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
